import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
} from "framer-motion";
import {
  Wifi, Check, ArrowRight, Zap, HardDrive, Wrench, Router, Headphones,
  CheckCircle2, ShieldCheck, Gauge, Signal, X,
  Star, Phone, MessageSquare, MapPin, Quote, Loader2, ThumbsUp, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, Lock, Tv,
} from "lucide-react";
import AddressAutocomplete from "./AddressAutocomplete";
import { ThemeToggle } from "./ThemeContext";
import TawkChat from "./TawkChat";

/* ─────────────────────────  DESIGN TOKENS  ───────────────────────── */
const C = {
  bg: "var(--bg)",
  surface: "var(--surface)",
  elevated: "var(--elevated)",
  blue: "#0055FF",
  blueHover: "#3377FF",
  cyan: "var(--cyan)",
  green: "#22C55E",
  yellow: "#F59E0B",
  white: "var(--text)",
  sec: "var(--text-sec)",
  muted: "var(--text-muted)",
};
const FH = "'Space Grotesk', sans-serif";
const FB = "'Outfit', sans-serif";
const FM = "'IBM Plex Mono', monospace";

const HERO_BG = "https://images.unsplash.com/photo-1731113993553-f3e82bfe5516?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FEATURED_BG = "https://images.pexels.com/photos/12489187/pexels-photo-12489187.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FAMILY_IMG = "https://images.unsplash.com/photo-1758598738092-a7cd486baadd?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
// Local Chatham-Kent rural imagery
const LOCAL_AERIAL = "https://images.pexels.com/photos/37837588/pexels-photo-37837588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const LOCAL_BARN = "https://images.pexels.com/photos/14014747/pexels-photo-14014747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// ── Contact ──
const PHONE = "(226) 626-3220";
const PHONE_TEL = "12266263220";

// ── Reviews ── Real Google reviews (Briarwood Internet)
const RATING = "4.8";
const REVIEW_COUNT = 9;
const REVIEWS_URL = "https://www.google.com/search?q=Briarwood+Internet+Chatham-Kent+Ontario+reviews";
const REVIEWS = [
  { name: "Dawson Marchand", stars: 5, text: "Great company — we just switched to Briarwood a few weeks ago and could not be happier with their customer care and quality." },
  { name: "Zoomin", stars: 5, text: "Fastest internet around, no one has anything like it. I finally got really fast internet, and we live in the middle of nowhere." },
  { name: "Chris Lovell", stars: 4, text: "We've been extremely pleased with the service and support we've received. The price savings are unmatched by any of the larger providers." },
  { name: "Carmela Gasparini", stars: 4, text: "I've been with Briarwood Internet since November 2023 and am very satisfied. The support is always reliable and problems are solved with a quick response. Highly recommend." },
  { name: "Tim", stars: 5, text: "Very prompt to reply to messages and help with any problem — even when we were at our Florida home, he's always there to help." },
];

const FEATURES = [
  { icon: HardDrive, label: "Unlimited Data" },
  { icon: Zap, label: "Low Latency" },
  { icon: Wrench, label: "Free Installation" },
  { icon: Router, label: "Free WiFi Router" },
  { icon: Headphones, label: "24/7 Tech Support" },
];
const PLAN_FEATURES = ["Unlimited Data", "Low Latency", "Free Installation", "Free WiFi Router", "24/7 Tech Support"];

const PLANS = [
  { speed: "50", displaySpeed: "50", unit: "Mbps", price: "50" },
  { speed: "100", displaySpeed: "100", unit: "Mbps", price: "75" },
  { speed: "300", displaySpeed: "300", unit: "Mbps", price: "100" },
  { speed: "500", displaySpeed: "500", unit: "Mbps", price: "150" },
];
const FEATURED = {
  speed: "1000", displaySpeed: "1", unit: "Gbps", price: "79.99",
  contract: "2-Year Contract",
};

/* ── Standalone add-on services (also mirrored as Offers in public/index.html) ── */
const ADDONS = [
  {
    key: "home-phone", icon: "phone", name: "Home Phone", price: "25",
    tagline: "Crystal-clear home phone on our network — no internet plan required.",
    features: ["Unlimited Canada-wide calling", "Keep your current number", "Voicemail & caller ID included"],
  },
  {
    key: "tv", icon: "tv", name: "TV", price: "25",
    tagline: "Your favourite channels, none of the clutter — a standalone TV service.",
    features: ["Local + popular channels", "Watch on your TV or devices", "No extra equipment fees"],
  },
];

/* ── FAQ (also mirrored as FAQPage JSON-LD in public/index.html) ── */
const FAQ_ITEMS = [
  { q: "How do I know if you cover my address?", a: "Use the coverage checker at the top of this page — enter your address and we'll instantly check service availability at your location. Either way you can pick a plan and hit Get Started, and we'll reach out to get you connected." },
  { q: "What speeds and prices do you offer?", a: "Plans start at 50 Mbps for $50/month and go up to our 1 Gbps Best Value plan at $79.99/month on a 2-year term, with 100, 300 and 500 Mbps options in between. Every plan includes unlimited data." },
  { q: "Is there a contract or an installation fee?", a: "Our standard monthly plans have no long-term contract — only the 1 Gbps Best Value plan is on a 2-year term. Installation is always free and includes a free WiFi router." },
  { q: "Is the data really unlimited?", a: "Yes. Every Briarwood plan comes with truly unlimited data — no caps, no throttling, and no surprise overage fees." },
  { q: "What if trees or distance affect my signal?", a: "Our coverage check accounts for line-of-sight, and if your location is borderline we'll arrange a free on-site assessment before you commit. And if you ever need a hand, our support is local and available 24/7." },
];

function FaqSection() {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" data-testid="faq-section" style={{ padding: "84px 24px", background: `linear-gradient(180deg, ${C.bg}, ${C.surface})` }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 16 }}>Questions & Answers</div>
          <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(30px,4vw,48px)", letterSpacing: "-0.035em", margin: 0 }}>Frequently asked.</h2>
        </motion.div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div key={i} data-testid={"faq-item-" + i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                style={{ background: C.elevated, border: `1px solid ${isOpen ? "rgba(0,229,255,0.35)" : "var(--w8)"}`, borderRadius: 16, overflow: "hidden" }}>
                <button data-testid={"faq-question-" + i} onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "20px 22px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <span style={{ fontFamily: FB, fontWeight: 600, fontSize: 16.5, color: C.white, lineHeight: 1.4 }}>{item.q}</span>
                  <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }} style={{ flexShrink: 0, color: isOpen ? C.cyan : C.muted, display: "flex" }}>
                    <ChevronDown size={20} strokeWidth={2.4} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div key="a" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
                      <p data-testid={"faq-answer-" + i} style={{ margin: 0, padding: "0 22px 22px", fontSize: 15.5, color: C.sec, lineHeight: 1.7, fontWeight: 300 }}>{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* motion helpers */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

/* ─────────────────────────  FEATURED CARD  ───────────────────────── */
function FeaturedPlanCard({ plan, label, onClick }) {
  const features = (plan.features && plan.features.length) ? plan.features : PLAN_FEATURES;
  return (
    <motion.div
      data-testid={"pricing-card-" + plan.speed}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeUp}
      style={{
        marginBottom: 24,
      }}
    >
      <motion.div
        style={{
          position: "relative", overflow: "hidden",
          borderRadius: 24,
          border: "1px solid rgba(0,229,255,0.35)",
          background: `linear-gradient(135deg, var(--g92), var(--e92)), url(${FEATURED_BG})`,
          backgroundSize: "cover", backgroundPosition: "center",
          boxShadow: "0 0 0 1px rgba(0,85,255,0.15), 0 30px 90px rgba(0,85,255,0.28), inset 0 0 60px rgba(0,85,255,0.08)",
          padding: "clamp(32px, 4vw, 56px)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 40,
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(560px circle at 50% 45%, rgba(0,229,255,0.16), transparent 45%)", pointerEvents: "none" }} />

        <div style={{ position: "absolute", top: 22, left: "clamp(32px,4vw,56px)", display: "flex", gap: 8, zIndex: 2 }}>
          <span style={{
            background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001",
            fontFamily: FM, fontSize: 10, fontWeight: 600, letterSpacing: "0.16em",
            textTransform: "uppercase", padding: "6px 16px", borderRadius: 100,
            boxShadow: `0 0 22px rgba(0,229,255,0.4)`,
          }}>Best Value</span>
          {plan.contract && (
            <span style={{
              background: "var(--w6)", color: C.sec, border: "1px solid var(--w14)",
              fontFamily: FM, fontSize: 10, fontWeight: 600, letterSpacing: "0.12em",
              textTransform: "uppercase", padding: "6px 14px", borderRadius: 100,
            }}>{plan.contract}</span>
          )}
        </div>

        {/* Speed + price */}
        <div style={{ position: "relative", zIndex: 2, paddingTop: 34 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, lineHeight: 0.85 }}>
            <span style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(84px,11vw,132px)", color: C.white, letterSpacing: "-0.05em", textShadow: `0 0 60px ${C.blue}55` }}>{plan.displaySpeed}</span>
            <span style={{ fontFamily: FM, fontWeight: 600, fontSize: 30, color: C.cyan, marginBottom: 14, letterSpacing: "-0.02em" }}>{plan.unit}</span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 18 }}>
            <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 46, color: C.white, letterSpacing: "-0.03em" }}>${plan.price}</span>
            <span style={{ fontSize: 16, color: C.muted }}>/mo</span>
          </div>
          {plan.tagline && (
            <p style={{ fontFamily: FM, fontSize: 12, color: C.muted, marginTop: 10, letterSpacing: "0.04em" }}>
              {plan.tagline}
            </p>
          )}
        </div>

        {/* Features */}
        <ul style={{ position: "relative", zIndex: 2, listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 13, flex: "1 1 240px" }}>
          {features.map((f) => (
            <li key={f} style={{ display: "flex", alignItems: "center", gap: 11, fontFamily: FB, fontSize: 15, color: "var(--w82)" }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(0,229,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Check size={12} color={C.cyan} strokeWidth={3} />
              </span>{f}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: 12 }}>
          <motion.button
            onClick={onClick}
            data-testid="plan-select-1000"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{
              padding: "17px 48px", borderRadius: 14, border: "none", cursor: "pointer",
              background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018",
              fontFamily: FB, fontWeight: 700, fontSize: 16, whiteSpace: "nowrap",
              boxShadow: `0 10px 40px ${C.blue}88`,
            }}
          >{label}</motion.button>
          {plan.contract && (
            <span style={{ fontFamily: FM, fontSize: 11, color: C.muted, letterSpacing: "0.04em", textAlign: "center" }}>Requires {plan.contract.toLowerCase().includes("year") ? plan.contract.toLowerCase() : plan.contract}</span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────  MAIN PAGE  ───────────────────────── */
export default function LandingPage() {
  const [address, setAddress] = useState("");
  const [addressValid, setAddressValid] = useState(false);
  const [addrError, setAddrError] = useState("");
  const [coords, setCoords] = useState(null);
  const [checkResult, setCheckResult] = useState(null); // null | 'checking' | 'covered' | 'not_covered'
  const [servedBy, setServedBy] = useState(null);

  const [signupModal, setSignupModal] = useState(null);
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupAddress, setSignupAddress] = useState("");
  const [signupSubmitted, setSignupSubmitted] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Live pricing (falls back to hardcoded defaults until fetched)
  const [internetPlans, setInternetPlans] = useState(PLANS);
  const [featuredPlan, setFeaturedPlan] = useState(FEATURED);
  const [addonPlans, setAddonPlans] = useState(ADDONS);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/plans`)
      .then((r) => r.json())
      .then((d) => {
        const all = Array.isArray(d?.plans) ? d.plans : [];
        const inet = all.filter((p) => p.category === "internet");
        const feat = inet.find((p) => p.featured) || null;
        const reg = inet.filter((p) => p !== feat); // everything except the one featured card
        const adds = all.filter((p) => p.category === "addon");
        // Drive directly from the API (allow empty) so admin hides/deletes take effect.
        setInternetPlans(reg);
        setFeaturedPlan(feat);
        setAddonPlans(adds);
      })
      .catch(() => { /* keep hardcoded defaults on fetch failure */ });
  }, [API]);


  const reviewsRef = useRef(null);
  const scrollReviews = (dir) => {
    const el = reviewsRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const openSignup = (plan) => {
    setSignupModal(plan);
    setSignupName(""); setSignupEmail(""); setSignupPhone(""); setSignupAddress(address);
    setSignupSubmitted(false);
    setChatUnavailable(false);
  };
  const closeSignup = () => { setSignupModal(null); setSignupSubmitted(false); };

  const tawkRef = useRef(null);
  const [chatUnavailable, setChatUnavailable] = useState(false);
  const openLiveChat = () => {
    try {
      // showWidget() only makes the bubble icon visible; it does NOT open
      // the chat panel — that's a separate "state" controlled by maximize().
      // Both are needed so the chat actually opens in one click. The panel's
      // on-screen SIZE is a separate setting in the Tawk dashboard (Chat
      // Widget → Appearance → Advanced) — by default it's a small floating
      // window, not full-screen, so this does not conflict with keeping the
      // widget compact.
      tawkRef.current?.showWidget?.();
      tawkRef.current?.maximize?.();
      setChatUnavailable(false);
      closeSignup();
    } catch (e) {
      setChatUnavailable(true);
    }
  };

  const submitInquiry = async () => {
    if (!address.trim()) return;
    if (!addressValid) {
      setAddrError("Please pick your address from the suggestions (or use your current location) so we can confirm it's correct.");
      return;
    }
    setAddrError("");

    // No coordinates (manual entry / SDK unavailable) — skip the check, funnel to plans.
    if (!coords) {
      setServedBy(null);
      setCheckResult("covered");
      setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" }), 1000);
      return;
    }

    setCheckResult("checking");
    try {
      const res = await fetch(`${API}/api/check-coverage`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat: coords.lat, lng: coords.lng, address }),
      });
      if (!res.ok) throw new Error("coverage service unavailable");
      const data = await res.json();
      // Covered → confident messaging. Not covered / borderline → soft messaging.
      // Both funnel to the plans + Get Started flow; the result is logged for the admin.
      if (data.covered || data.serviceable) {
        setServedBy(data.served_by || null);
        setCheckResult("covered");
      } else {
        setServedBy(null);
        setCheckResult("not_covered");
      }
      // GTM dataLayer event for Google Ads
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "coverage_check",
        coverage: (data.covered || data.serviceable) ? "covered" : "not_covered",
      });
    } catch (e) {
      // On failure, don't block the funnel — treat as reachable.
      setServedBy(null);
      setCheckResult("covered");
    }
    setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" }), 1200);
  };

  const resetChecker = () => { setCheckResult(null); setServedBy(null); };

  const planButtonState = (checkResult === "covered" || checkResult === "not_covered") ? "get-started" : "check-availability";
  const planButtonLabel = planButtonState === "get-started" ? "Get Started" : "Check Availability";

  const scrollToChecker = () =>
    document.getElementById("check-availability")?.scrollIntoView({ behavior: "smooth", block: "center" });

  // Human-readable plan label for internet plans ("500 Mbps", "1 Gbps") and add-ons ("Home Phone").
  const planLabel = (p) => (p && p.displaySpeed) ? `${p.displaySpeed} ${p.unit}` : (p?.name || "");

  const handlePlanButton = (plan) => {
    if (planButtonState === "get-started") {
      // GTM dataLayer event: which plan a visitor selected to sign up for
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "plan_selected",
        plan_speed: plan?.speed ?? null,
        plan_price: plan?.price ?? null,
        plan_name: planLabel(plan) || null,
        coverage: checkResult === "covered" ? "covered" : "not_covered",
      });
      openSignup(plan);
    } else scrollToChecker();
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!signupName.trim() || !signupEmail.trim()) return;
    setIsSigningUp(true);
    try {
      const res = await fetch(`${API}/api/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: signupName, email: signupEmail, phone: signupPhone, address: signupAddress,
          plan_speed: planLabel(signupModal), plan_price: signupModal?.price,
          lat: coords?.lat ?? null, lng: coords?.lng ?? null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSignupSubmitted(true);
        // GTM dataLayer event for Google Ads conversion tracking
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "generate_lead",
          lead_type: "get_started_signup",
          plan_speed: signupModal?.speed ?? null,
          plan_price: signupModal?.price ?? null,
          coverage: checkResult === "covered" ? "covered" : "not_covered",
        });
      }
    } catch (e) {
    } finally { setIsSigningUp(false); }
  };

  const darkInput = {
    padding: "14px 16px", borderRadius: 12, width: "100%",
    border: "1px solid var(--w12)", fontSize: 15,
    fontFamily: FB, outline: "none", color: C.white,
    background: "var(--w3)", transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box",
  };
  const onInFocus = (e) => { e.target.style.borderColor = C.blue; e.target.style.boxShadow = `0 0 0 3px ${C.blue}22`; };
  const onInBlur = (e) => { e.target.style.borderColor = "var(--w12)"; e.target.style.boxShadow = "none"; };

  return (
    <div style={{ fontFamily: FB, background: C.bg, color: C.white, overflowX: "hidden" }}>
      <style>{`
        @keyframes ping { 75%,100% { transform: scale(2.2); opacity: 0; } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        [data-testid="reviews-track"]::-webkit-scrollbar { display: none; }
        @media (prefers-reduced-motion: reduce) { [data-testid="availability-section"] { animation: none !important; } }
        @keyframes bgpan { 0%{ transform: scale(1.1) translate(0,0);} 50%{ transform: scale(1.18) translate(-2%,-2%);} 100%{ transform: scale(1.1) translate(0,0);} }
        select option { background: ${C.elevated}; color: ${C.white}; }
        ::selection { background: ${C.blue}; color: #fff; }
        html { scroll-behavior: smooth; }
      `}</style>

      <TawkChat ref={tawkRef} />

      {/* ── HEADER ─────────────────────────────────────────── */}
      <header data-testid="main-header" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: "var(--k60)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--w7)",
      }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: isMobile ? "0 16px" : "0 24px", height: 66, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 20px ${C.blue}66` }}>
              <Wifi size={18} color="#001" strokeWidth={2.6} />
            </div>
            <span style={{ fontFamily: FH, fontWeight: 700, fontSize: isMobile ? 16 : 18, letterSpacing: "-0.02em" }}>Briarwood Internet</span>
          </div>
          <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 10 }}>
            {!isMobile && <a href="#pricing" style={{ fontFamily: FB, fontSize: 13, fontWeight: 500, color: C.sec, textDecoration: "none", padding: "8px 12px" }}>Plans</a>}
            {!isMobile && <button onClick={scrollToChecker} data-testid="header-check-coverage" style={{ fontFamily: FB, fontSize: 13, fontWeight: 500, color: C.sec, textDecoration: "none", padding: "8px 12px", background: "none", border: "none", cursor: "pointer" }}>Check Coverage</button>}
            {!isMobile && <a href="#faq" data-testid="header-faq-link" style={{ fontFamily: FB, fontSize: 13, fontWeight: 500, color: C.sec, textDecoration: "none", padding: "8px 12px" }}>FAQ</a>}
            <ThemeToggle />
            <a href={`tel:+${PHONE_TEL}`} data-testid="header-call-btn" style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 10,
              background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018",
              textDecoration: "none", fontFamily: FB, fontSize: 13, fontWeight: 700,
              boxShadow: `0 6px 20px ${C.blue}55`, whiteSpace: "nowrap",
            }}>
              <Phone size={14} strokeWidth={2.6} /> Call Us
            </a>
            {!isMobile && (
              <Link to="/status" data-testid="header-status-link" style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 10,
                border: "1px solid var(--w12)", background: "var(--w3)",
                textDecoration: "none", fontFamily: FM, fontSize: 12, fontWeight: 500, color: C.white, letterSpacing: "0.02em",
              }}>
                <span style={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
                  <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: C.green, animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite", opacity: 0.75 }} />
                  <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: C.green }} />
                </span>
                Network Status
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────────────────── */}
      <section data-testid="hero-section" style={{ position: "relative", minHeight: "100vh", paddingTop: 66, display: "flex", alignItems: "center", overflow: "hidden" }}>
        {/* animated bg */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: "-10%", backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center 35%", opacity: 0.9, animation: "bgpan 24s ease-in-out infinite" }} />
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 28% 45%, rgba(0,85,255,0.18), transparent 55%), radial-gradient(circle at 88% 72%, rgba(0,229,255,0.12), transparent 50%), linear-gradient(90deg, var(--k92) 0%, var(--k66) 42%, var(--k32) 100%), linear-gradient(180deg, var(--k35) 0%, var(--k0) 45%, ${C.bg} 100%)` }} />
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(var(--w5) 1px, transparent 1px)", backgroundSize: "38px 38px", maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)", WebkitMaskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)" }} />
        </div>

        <div style={{ position: "relative", zIndex: 2, maxWidth: 1240, margin: "0 auto", padding: "120px 24px 80px", width: "100%", display: "flex", alignItems: "center", gap: 56, flexWrap: "wrap" }}>
          {/* Left copy */}
          <motion.div initial="hidden" animate="show" style={{ flex: "1 1 500px", minWidth: 0 }}>
            <motion.div custom={0} variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "7px 15px", borderRadius: 100, background: "rgba(0,85,255,0.1)", border: "1px solid rgba(0,85,255,0.25)", marginBottom: 30 }}>
              <Signal size={13} color={C.cyan} />
              <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.14em", textTransform: "uppercase" }}>Chatham-Kent, Ontario</span>
            </motion.div>

            <motion.h1 custom={1} variants={fadeUp} style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(48px,7vw,90px)", lineHeight: 0.98, letterSpacing: "-0.045em", margin: "0 0 26px" }}>
              Internet that<br />
              <span style={{ background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>actually flies.</span>
            </motion.h1>

            <motion.p custom={2} variants={fadeUp} style={{ fontSize: 19, color: C.sec, lineHeight: 1.65, maxWidth: 480, marginBottom: 38, fontWeight: 300 }}>
              Up to 1 Gbps of fibre-grade wireless, built for Chatham-Kent. Unlimited data, no runaround — just pick a plan and get online.
            </motion.p>

            <motion.div custom={3} variants={fadeUp} style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <motion.button onClick={scrollToChecker} data-testid="cta-hero" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{
                display: "inline-flex", alignItems: "center", gap: 9, padding: "16px 30px", borderRadius: 12, border: "none", cursor: "pointer",
                background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 15,
                boxShadow: `0 10px 40px ${C.blue}77`,
              }}>Check My Coverage <ArrowRight size={17} strokeWidth={2.6} /></motion.button>
              <a href="#pricing" style={{ display: "inline-flex", alignItems: "center", padding: "16px 28px", borderRadius: 12, border: "1px solid var(--w14)", background: "var(--w3)", color: C.white, fontFamily: FB, fontWeight: 600, fontSize: 15, textDecoration: "none" }}>See Plans</a>
            </motion.div>

            <motion.div custom={4} variants={fadeUp} style={{ display: "flex", gap: 36, marginTop: 52, paddingTop: 30, borderTop: "1px solid var(--w8)", flexWrap: "wrap" }}>
              {[["1 Gbps", "Top Speed"], ["$50/mo", "Starting At"], ["Unlimited", "No Data Caps"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 24, letterSpacing: "-0.02em" }}>{v}</div>
                  <div style={{ fontFamily: FM, fontSize: 11, color: C.muted, marginTop: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>{l}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: floating availability checker */}
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ flex: "1 1 400px", minWidth: 300, display: "flex", justifyContent: "center" }}>
            <div id="check-availability" data-testid="availability-section" style={{ width: "100%", maxWidth: 440, animation: "floaty 6s ease-in-out infinite" }}>
              <div style={{
                position: "relative", borderRadius: 22, padding: 32,
                background: "var(--g72)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
                border: "1px solid var(--w10)", boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 0 40px rgba(0,85,255,0.06)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <Gauge size={17} color={C.cyan} />
                  <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.14em", textTransform: "uppercase" }}>Instant Coverage Check</span>
                </div>
                <h3 style={{ fontFamily: FH, fontWeight: 700, fontSize: 24, letterSpacing: "-0.02em", margin: "0 0 20px" }}>Can we reach you?</h3>

                <AnimatePresence mode="wait">
                  {checkResult === null && (
                    <motion.div key="input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                      <div style={{ marginBottom: 12 }}>
                        <AddressAutocomplete
                          value={address}
                          onChange={setAddress}
                          onValidChange={(v) => { setAddressValid(v); if (v) setAddrError(""); }}
                          onCoords={setCoords}
                          onSubmit={submitInquiry}
                          inputStyle={{ ...darkInput, paddingRight: 44 }}
                        />
                      </div>
                      <motion.button onClick={submitInquiry} data-testid="check-availability-btn" disabled={!address.trim()} whileHover={address.trim() ? { scale: 1.02 } : {}} whileTap={address.trim() ? { scale: 0.98 } : {}} style={{
                        width: "100%", padding: "15px", borderRadius: 12, border: "none",
                        cursor: address.trim() ? "pointer" : "not-allowed",
                        background: address.trim() ? `linear-gradient(90deg, ${C.blue}, ${C.cyan})` : "var(--w6)",
                        color: address.trim() ? "#001018" : C.muted, fontFamily: FB, fontWeight: 700, fontSize: 15,
                        boxShadow: address.trim() ? `0 8px 30px ${C.blue}55` : "none",
                      }}>
                        Check Availability
                      </motion.button>
                      {addrError && (
                        <p data-testid="address-error" style={{ margin: "10px 0 0", fontFamily: FB, fontSize: 12, color: C.yellow, lineHeight: 1.4, textAlign: "center" }}>{addrError}</p>
                      )}
                      <p style={{ fontFamily: FM, fontSize: 11, color: C.muted, textAlign: "center", marginTop: 14, letterSpacing: "0.03em" }}>No commitment · takes 2 seconds</p>
                    </motion.div>
                  )}

                  {checkResult === "checking" && (
                    <motion.div key="checking" data-testid="availability-checking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ textAlign: "center", padding: "24px 0" }}>
                      <Loader2 size={38} color={C.cyan} strokeWidth={2} style={{ margin: "0 auto 18px", display: "block", animation: "spin 0.9s linear infinite" }} />
                      <h4 style={{ fontFamily: FH, fontWeight: 700, fontSize: 19, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Checking availability…</h4>
                      <p style={{ fontSize: 14, color: C.sec, margin: 0, lineHeight: 1.6 }}>Checking service availability at <strong style={{ color: C.white }}>{address}</strong></p>
                    </motion.div>
                  )}

                  {checkResult === "covered" && (
                    <motion.div key="covered" data-testid="availability-result-covered" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} style={{ textAlign: "center" }}>
                      <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(34,197,94,0.14)", display: "flex", alignItems: "center", justifyContent: "center", margin: "4px auto 18px", boxShadow: `0 0 30px ${C.green}44` }}>
                        <CheckCircle2 size={30} color={C.green} strokeWidth={2} />
                      </div>
                      <h4 style={{ fontFamily: FH, fontWeight: 700, fontSize: 20, margin: "0 0 10px", letterSpacing: "-0.02em" }}>You're covered!</h4>
                      <p style={{ fontSize: 14, color: C.sec, margin: "0 0 6px", lineHeight: 1.6 }}>Great news — <strong style={{ color: C.white }}>{address}</strong> is within our coverage area.</p>
                      {servedBy && (
                        <p data-testid="served-by" style={{ fontFamily: FM, fontSize: 12, color: C.cyan, margin: "0 0 20px", letterSpacing: "0.04em" }}>Service confirmed for your address</p>
                      )}
                      <a href="#pricing" data-testid="availability-see-plans-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 11, background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 14, textDecoration: "none", marginTop: servedBy ? 0 : 14 }}>See Plans <ArrowRight size={15} strokeWidth={2.6} /></a>
                      <button onClick={resetChecker} data-testid="check-another-btn" style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: C.muted, fontFamily: FB, fontSize: 13, cursor: "pointer" }}>Check another address</button>
                    </motion.div>
                  )}

                  {checkResult === "not_covered" && (
                    <motion.div key="not_covered" data-testid="availability-result-not-covered" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} style={{ textAlign: "center" }}>
                      <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(0,229,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "4px auto 18px", boxShadow: `0 0 30px ${C.cyan}44` }}>
                        <Signal size={28} color={C.cyan} strokeWidth={2} />
                      </div>
                      <h4 style={{ fontFamily: FH, fontWeight: 700, fontSize: 20, margin: "0 0 10px", letterSpacing: "-0.02em" }}>We may have coverage in your area!</h4>
                      <p style={{ fontSize: 14, color: C.sec, margin: "0 0 22px", lineHeight: 1.6 }}>Select your plan for <strong style={{ color: C.white }}>{address}</strong> and we'll reach out to get you started!</p>
                      <a href="#pricing" data-testid="availability-see-plans-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 11, background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>See Plans <ArrowRight size={15} strokeWidth={2.6} /></a>
                      <button onClick={resetChecker} data-testid="check-another-btn" style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: C.muted, fontFamily: FB, fontSize: 13, cursor: "pointer" }}>Check another address</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── MARQUEE FEATURES ───────────────────────────────── */}
      <section data-testid="features-strip" style={{ background: C.surface, borderTop: "1px solid var(--w6)", borderBottom: "1px solid var(--w6)", padding: "22px 0", overflow: "hidden" }}>
        <div style={{ display: "flex", width: "max-content", animation: "marquee 28s linear infinite" }}>
          {[...FEATURES, ...FEATURES, ...FEATURES, ...FEATURES].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={`${f.label}-${i}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "0 44px", flexShrink: 0, borderRight: "1px solid var(--w7)" }}>
                <Icon size={16} color={C.cyan} strokeWidth={2} />
                <span style={{ fontFamily: FM, fontSize: 12, fontWeight: 500, color: C.sec, whiteSpace: "nowrap", letterSpacing: "0.08em", textTransform: "uppercase" }}>{f.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────── */}
      <section id="pricing" data-testid="pricing-section" style={{ position: "relative", padding: "110px 24px", background: `linear-gradient(180deg, ${C.bg}, ${C.surface})` }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 16 }}>Simple Pricing</div>
            <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(34px,4.5vw,56px)", letterSpacing: "-0.035em", margin: "0 0 16px" }}>Pick your speed.</h2>
            <p style={{ fontSize: 16, color: C.sec, maxWidth: 460, margin: "0 auto 22px", fontWeight: 300 }}>Every plan includes unlimited data, free install, and a free WiFi router.</p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 100, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.22)" }}>
              <ShieldCheck size={15} color={C.green} strokeWidth={2.2} />
              <span style={{ fontFamily: FM, fontSize: 12, fontWeight: 600, color: C.green, letterSpacing: "0.04em" }}>30-Day Money-Back Guarantee</span>
            </div>
          </motion.div>

          {featuredPlan && <FeaturedPlanCard plan={featuredPlan} label={planButtonLabel} onClick={() => handlePlanButton(featuredPlan)} />}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 18 }}>
            {internetPlans.map((plan, i) => (
              <motion.div key={plan.id || plan.speed} data-testid={"pricing-card-" + plan.speed}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} custom={i} variants={fadeUp}
                whileHover={{ y: -8, boxShadow: `0 24px 60px rgba(0,85,255,0.22), inset 0 0 0 1px ${C.blue}55` }}
                style={{ background: C.elevated, border: "1px solid var(--w8)", borderRadius: 18, padding: "30px 26px", display: "flex", flexDirection: "column" }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 44, letterSpacing: "-0.04em" }}>{plan.displaySpeed}</span>
                  <span style={{ fontFamily: FM, fontSize: 15, color: C.muted, marginLeft: 5 }}>{plan.unit}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 6 }}>
                  <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 28, letterSpacing: "-0.02em" }}>${plan.price}</span>
                  <span style={{ fontSize: 14, color: C.muted }}>/mo</span>
                </div>
                <p style={{ fontFamily: FM, fontSize: 12, color: C.muted, marginBottom: 22, letterSpacing: "0.02em" }}>{plan.tagline || `${plan.displaySpeed} ${plan.unit} symmetric`}</p>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 26px", display: "flex", flexDirection: "column", gap: 11 }}>
                  {((plan.features && plan.features.length) ? plan.features : PLAN_FEATURES).map((f) => (
                    <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.sec }}>
                      <Check size={14} color={C.blue} strokeWidth={2.8} style={{ flexShrink: 0 }} />{f}
                    </li>
                  ))}
                </ul>
                <motion.button onClick={() => handlePlanButton(plan)} data-testid={"plan-select-" + plan.speed} whileTap={{ scale: 0.97 }} style={{
                  width: "100%", padding: "13px 0", borderRadius: 11, marginTop: "auto", cursor: "pointer",
                  background: planButtonState === "get-started" ? `linear-gradient(90deg, ${C.blue}, ${C.cyan})` : "transparent",
                  border: planButtonState === "get-started" ? "none" : `1px solid ${C.blue}`,
                  color: planButtonState === "get-started" ? "#001018" : C.cyan, fontFamily: FB, fontWeight: 700, fontSize: 14,
                }}>{planButtonLabel}</motion.button>
                <p style={{ textAlign: "center", fontFamily: FM, fontSize: 11, color: C.muted, margin: "10px 0 0", letterSpacing: "0.03em" }}>{plan.contract ? `Requires ${plan.contract.toLowerCase()}` : "No commitment required"}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STANDALONE ADD-ONS (Home Phone / TV) ───────────── */}
      <section id="addons" data-testid="addons-section" style={{ position: "relative", padding: "0 24px 110px", background: `linear-gradient(180deg, ${C.surface}, ${C.bg})` }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: "center", marginBottom: 44 }}>
            <div style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 16 }}>Also Available</div>
            <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(30px,4vw,48px)", letterSpacing: "-0.035em", margin: "0 0 16px" }}>Add-on services.</h2>
            <p style={{ fontSize: 16, color: C.sec, maxWidth: 480, margin: "0 auto", fontWeight: 300 }}>Optional Home Phone and TV services — no internet plan required.</p>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18, maxWidth: 780, margin: "0 auto" }}>
            {addonPlans.map((addon, i) => {
              const AddonIcon = addon.icon === "tv" ? Tv : Phone;
              return (
                <motion.div key={addon.id || addon.key} data-testid={"addon-card-" + addon.key}
                  initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} custom={i} variants={fadeUp}
                  whileHover={{ y: -8, boxShadow: `0 24px 60px rgba(0,85,255,0.22), inset 0 0 0 1px ${C.blue}55` }}
                  style={{ background: C.elevated, border: "1px solid var(--w8)", borderRadius: 18, padding: "30px 26px", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,85,255,0.12)", border: `1px solid ${C.blue}44` }}>
                      <AddonIcon size={22} color={C.cyan} strokeWidth={2.2} />
                    </div>
                    <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 24, letterSpacing: "-0.03em" }}>{addon.name}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 10 }}>
                    <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 30, letterSpacing: "-0.02em" }}>${addon.price}</span>
                    <span style={{ fontSize: 14, color: C.muted }}>/mo</span>
                  </div>
                  <p style={{ fontFamily: FB, fontSize: 14, color: C.sec, lineHeight: 1.5, marginBottom: 22 }}>{addon.tagline}</p>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 26px", display: "flex", flexDirection: "column", gap: 11 }}>
                    {(addon.features || []).map((f) => (
                      <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.sec }}>
                        <Check size={14} color={C.blue} strokeWidth={2.8} style={{ flexShrink: 0 }} />{f}
                      </li>
                    ))}
                  </ul>
                  <motion.button onClick={() => handlePlanButton(addon)} data-testid={"addon-select-" + addon.key} whileTap={{ scale: 0.97 }} style={{
                    width: "100%", padding: "13px 0", borderRadius: 11, marginTop: "auto", cursor: "pointer",
                    background: planButtonState === "get-started" ? `linear-gradient(90deg, ${C.blue}, ${C.cyan})` : "transparent",
                    border: planButtonState === "get-started" ? "none" : `1px solid ${C.blue}`,
                    color: planButtonState === "get-started" ? "#001018" : C.cyan, fontFamily: FB, fontWeight: 700, fontSize: 14,
                  }}>{planButtonLabel}</motion.button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── NEIGHBOUR REVIEWS ──────────────────────────────── */}
      <section data-testid="reviews-section" style={{ padding: "96px 24px 60px", background: `linear-gradient(180deg, ${C.surface}, ${C.bg})` }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 16 }}>From the Neighbours</div>
            <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(30px,4vw,48px)", letterSpacing: "-0.035em", margin: "0 0 18px" }}>Loved across Chatham-Kent.</h2>
            <a href={REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-rating-link" style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "10px 20px", borderRadius: 100, background: "var(--w4)", border: "1px solid var(--w10)", textDecoration: "none" }}>
              <div style={{ display: "flex", gap: 2 }}>
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} color={C.yellow} fill={C.yellow} strokeWidth={0} />)}
              </div>
              <span style={{ fontFamily: FM, fontSize: 13, fontWeight: 600, color: C.white }}>{RATING}</span>
              <span style={{ fontSize: 13, color: C.muted }}>· {REVIEW_COUNT} Google reviews</span>
              <ExternalLink size={13} color={C.cyan} strokeWidth={2.4} />
            </a>
          </motion.div>

          <div style={{ position: "relative" }}>
            <button onClick={() => scrollReviews(-1)} data-testid="reviews-prev" aria-label="Previous reviews" style={{ position: "absolute", left: -6, top: "50%", transform: "translateY(-50%)", zIndex: 5, width: 44, height: 44, borderRadius: "50%", border: "1px solid var(--w14)", background: "var(--g85)", backdropFilter: "blur(8px)", color: C.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
              <ChevronLeft size={20} strokeWidth={2.4} />
            </button>
            <button onClick={() => scrollReviews(1)} data-testid="reviews-next" aria-label="Next reviews" style={{ position: "absolute", right: -6, top: "50%", transform: "translateY(-50%)", zIndex: 5, width: 44, height: 44, borderRadius: "50%", border: "1px solid var(--w14)", background: "var(--g85)", backdropFilter: "blur(8px)", color: C.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
              <ChevronRight size={20} strokeWidth={2.4} />
            </button>
            <div ref={reviewsRef} data-testid="reviews-track" style={{ display: "flex", gap: 18, overflowX: "auto", scrollSnapType: "x mandatory", scrollBehavior: "smooth", padding: "4px 2px 14px", scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {REVIEWS.map((r, i) => (
                <motion.div key={r.name} data-testid={"review-card-" + i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  whileHover={{ y: -6 }}
                  style={{ flex: "0 0 clamp(280px, 82vw, 340px)", scrollSnapAlign: "start", background: C.elevated, border: "1px solid var(--w8)", borderRadius: 18, padding: 30, display: "flex", flexDirection: "column", gap: 16, position: "relative", overflow: "hidden" }}>
                  <Quote size={44} color={C.blue} style={{ position: "absolute", top: 16, right: 18, opacity: 0.12 }} />
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 7, alignSelf: "flex-start", padding: "5px 12px", borderRadius: 100, background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.25)" }}>
                    <ThumbsUp size={12} color={C.green} strokeWidth={2.4} />
                    <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.green, letterSpacing: "0.03em" }}>Recommends</span>
                  </div>
                  <p style={{ fontSize: 15, color: "var(--w85)", lineHeight: 1.65, margin: 0, fontWeight: 300, position: "relative", zIndex: 1 }}>"{r.text}"</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: "auto", paddingTop: 8 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: FH, fontWeight: 700, fontSize: 16, color: "#001018" }}>{r.name.charAt(0)}</div>
                    <div>
                      <div style={{ fontFamily: FB, fontWeight: 600, fontSize: 14, color: C.white }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>via Google</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── LOCAL LANDSCAPE BAND ───────────────────────────── */}
      <section data-testid="local-band" style={{ position: "relative", minHeight: 420, display: "flex", alignItems: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
          <img src={LOCAL_AERIAL} alt="Chatham-Kent farmland at sunset" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${C.bg} 8%, var(--k72) 45%, var(--k35) 100%)` }} />
        </div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} style={{ position: "relative", zIndex: 2, maxWidth: 1240, margin: "0 auto", padding: "72px 24px", width: "100%" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "7px 15px", borderRadius: 100, background: "rgba(0,85,255,0.14)", border: "1px solid rgba(0,85,255,0.3)", marginBottom: 22 }}>
            <MapPin size={13} color={C.cyan} />
            <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.14em", textTransform: "uppercase" }}>Locally Owned and Operated</span>
          </div>
          <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(30px,4.4vw,54px)", letterSpacing: "-0.04em", margin: "0 0 18px", maxWidth: 760, lineHeight: 1.05 }}>
            Chatham is home.<br /><span style={{ color: C.cyan }}>That's why we do things differently.</span>
          </h2>
          <p style={{ fontSize: 17, color: "var(--w80)", lineHeight: 1.65, maxWidth: 640, margin: 0, fontWeight: 300 }}>
            We believe local service should mean more than a logo. It means real people, real accountability, and a network we're proud to use ourselves. When you choose Briarwood Internet, you're choosing neighbours who genuinely care about keeping you connected.
          </p>
        </motion.div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────── */}
      <FaqSection />

      {/* ── CALL/TEXT A LOCAL HUMAN BAND ───────────────────── */}
      <section data-testid="contact-band" style={{ padding: "64px 24px 84px", background: C.bg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            style={{ position: "relative", overflow: "hidden", borderRadius: 24, border: "1px solid rgba(0,85,255,0.25)",
              background: `linear-gradient(135deg, rgba(0,85,255,0.14), rgba(0,229,255,0.06)), ${C.elevated}`,
              padding: "clamp(32px,4vw,52px)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 36, flexWrap: "wrap",
              boxShadow: "inset 0 0 60px rgba(0,85,255,0.08)" }}>
            <div style={{ minWidth: 260, flex: "1 1 340px" }}>
              <div style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14 }}>Real people, right here</div>
              <h2 style={{ fontFamily: FH, fontWeight: 700, fontSize: "clamp(28px,3.6vw,42px)", letterSpacing: "-0.035em", margin: "0 0 12px" }}>Call or text a local human.</h2>
              <p style={{ fontSize: 16, color: C.sec, lineHeight: 1.6, margin: 0, fontWeight: 300, maxWidth: 440 }}>
                No phone trees, no offshore call centre. Reach a Chatham-Kent local who actually knows the network.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: "0 0 auto" }}>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 30, letterSpacing: "-0.02em", textAlign: "center", marginBottom: 2 }}>{PHONE}</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                <motion.a href={`tel:+${PHONE_TEL}`} data-testid="contact-call-btn" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "15px 30px", borderRadius: 12, background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 15, textDecoration: "none", boxShadow: `0 10px 34px ${C.blue}66` }}>
                  <Phone size={17} strokeWidth={2.4} /> Call Us
                </motion.a>
                <motion.a href={`sms:+${PHONE_TEL}`} data-testid="contact-text-btn" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "15px 30px", borderRadius: 12, background: "var(--w5)", border: "1px solid var(--w14)", color: C.white, fontFamily: FB, fontWeight: 700, fontSize: 15, textDecoration: "none" }}>
                  <MessageSquare size={17} strokeWidth={2.4} /> Text Us
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────── */}
      <footer data-testid="footer-section" style={{ background: C.bg, borderTop: "1px solid var(--w7)", padding: "72px 24px 44px" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 36, marginBottom: 48 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Wifi size={16} color="#001" strokeWidth={2.6} />
                </div>
                <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 17 }}>Briarwood Internet</span>
              </div>
              <p style={{ fontSize: 14, color: C.muted, maxWidth: 280, lineHeight: 1.6, margin: 0, fontWeight: 300 }}>Chatham-Kent's locally-owned high-speed wireless internet provider.</p>
            </div>
            <div style={{ display: "flex", gap: 56, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontFamily: FM, fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 16 }}>Service</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <a href="#pricing" style={{ fontSize: 14, color: C.sec, textDecoration: "none" }}>Plans &amp; Pricing</a>
                  <button onClick={scrollToChecker} style={{ fontSize: 14, color: C.sec, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", fontFamily: FB }}>Check Coverage</button>
                  <a href="#faq" style={{ fontSize: 14, color: C.sec, textDecoration: "none" }}>FAQ</a>
                  <Link to="/status" style={{ fontSize: 14, color: C.sec, textDecoration: "none" }}>Network Status</Link>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: FM, fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 16 }}>Contact</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <a href={`tel:+${PHONE_TEL}`} style={{ fontSize: 14, color: C.sec, textDecoration: "none" }}>{PHONE}</a>
                  <a href="mailto:james@briarwoodinternet.ca" style={{ fontSize: 14, color: C.sec, textDecoration: "none" }}>james@briarwoodinternet.ca</a>
                  <span style={{ fontSize: 14, color: C.muted }}>Chatham-Kent, Ontario</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--w6)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontSize: 13, color: C.muted }}>&copy; {new Date().getFullYear()} Briarwood Internet. All rights reserved.</span>
            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
              <span style={{ fontFamily: FM, fontSize: 11, color: C.muted, letterSpacing: "0.06em" }}>Chatham-Kent, Ontario, Canada</span>
              <Link to="/admin" data-testid="footer-admin-link" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FM, fontSize: 11, color: C.muted, textDecoration: "none", letterSpacing: "0.04em", border: "1px solid var(--w10)", padding: "5px 11px", borderRadius: 8 }}>
                <Lock size={11} /> Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ── GET STARTED MODAL ──────────────────────────────── */}
      <AnimatePresence>
        {signupModal && (
          <motion.div data-testid="signup-modal-overlay" onClick={closeSignup}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 300, background: "var(--k72)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <motion.div data-testid="signup-modal" onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 20 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              style={{ background: C.elevated, borderRadius: 22, width: "100%", maxWidth: 480, border: "1px solid var(--w10)", boxShadow: "0 40px 100px rgba(0,0,0,0.6)", overflow: "hidden" }}>
              <div style={{ position: "relative", padding: "26px 28px", background: `linear-gradient(135deg, rgba(0,85,255,0.25), rgba(0,229,255,0.1)), ${C.surface}`, borderBottom: "1px solid var(--w8)" }}>
                <button onClick={closeSignup} data-testid="signup-modal-close" style={{ position: "absolute", top: 16, right: 16, background: "var(--w8)", border: "none", color: C.white, width: 32, height: 32, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} /></button>
                <div style={{ fontFamily: FM, fontSize: 10, fontWeight: 600, color: C.cyan, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>Selected Plan</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 36, letterSpacing: "-0.04em" }}>{signupModal.displaySpeed ? <>{signupModal.displaySpeed} <span style={{ fontSize: 18, color: C.muted }}>{signupModal.unit}</span></> : signupModal.name}</span>
                  <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 22, color: C.cyan }}>${signupModal.price}<span style={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>/mo</span></span>
                </div>
              </div>
              <div style={{ padding: 28 }}>
                {!signupSubmitted ? (
                  <>
                    <p style={{ fontSize: 14, color: C.sec, margin: "0 0 20px", lineHeight: 1.5, fontWeight: 300 }}>Fill in your details so we can reach out and answer any questions you might have.</p>
                    <motion.button type="button" onClick={openLiveChat} data-testid="signup-live-chat-btn" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.cyan}55`, background: "rgba(0,229,255,0.08)", color: C.white, cursor: "pointer", fontFamily: FB, fontWeight: 600, fontSize: 14, marginBottom: chatUnavailable ? 10 : 16 }}>
                      <MessageSquare size={16} strokeWidth={2.3} color={C.cyan} /> Chat with us live
                    </motion.button>
                    {chatUnavailable && (
                      <p data-testid="signup-live-chat-unavailable" style={{ fontSize: 12.5, color: C.muted, margin: "0 0 16px", lineHeight: 1.5 }}>Live chat is unavailable right now — please use the form below and we'll reach out shortly.</p>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 16px" }}>
                      <div style={{ flex: 1, height: 1, background: "var(--w10)" }} />
                      <span style={{ fontFamily: FM, fontSize: 10, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>or fill out the form</span>
                      <div style={{ flex: 1, height: 1, background: "var(--w10)" }} />
                    </div>
                    <form onSubmit={submitSignup} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <input type="text" placeholder="Your name" required value={signupName} onChange={(e) => setSignupName(e.target.value)} data-testid="signup-name-input" style={darkInput} onFocus={onInFocus} onBlur={onInBlur} />
                      <input type="email" placeholder="Your email address" required value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} data-testid="signup-email-input" style={darkInput} onFocus={onInFocus} onBlur={onInBlur} />
                      <input type="tel" placeholder="Your phone number" value={signupPhone} onChange={(e) => setSignupPhone(e.target.value)} data-testid="signup-phone-input" style={darkInput} onFocus={onInFocus} onBlur={onInBlur} />
                      <input type="text" placeholder="Your address (e.g. 123 Main St, Chatham)" value={signupAddress} onChange={(e) => setSignupAddress(e.target.value)} data-testid="signup-address-input" style={darkInput} onFocus={onInFocus} onBlur={onInBlur} />
                      <motion.button type="submit" data-testid="signup-submit-btn" disabled={isSigningUp || !signupName.trim() || !signupEmail.trim()} whileTap={{ scale: 0.98 }} style={{ padding: 15, borderRadius: 12, border: "none", cursor: isSigningUp ? "not-allowed" : "pointer", background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`, color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 15, opacity: isSigningUp ? 0.7 : 1, marginTop: 4 }}>{isSigningUp ? "Submitting…" : "Request Service"}</motion.button>
                    </form>
                  </>
                ) : (
                  <div data-testid="signup-success" style={{ textAlign: "center", padding: "16px 0" }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(34,197,94,0.14)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: `0 0 30px ${C.green}44` }}>
                      <CheckCircle2 size={30} color={C.green} strokeWidth={2} />
                    </div>
                    <h3 style={{ fontFamily: FH, fontWeight: 700, fontSize: 21, margin: "0 0 10px", letterSpacing: "-0.02em" }}>Request received!</h3>
                    <p style={{ fontSize: 14, color: C.sec, lineHeight: 1.6, margin: "0 0 20px", fontWeight: 300 }}>We'll reach out to <strong style={{ color: C.white }}>{signupEmail}</strong> shortly to get you set up.</p>
                    <button onClick={closeSignup} style={{ padding: "11px 26px", borderRadius: 10, background: "var(--w8)", color: C.white, border: "1px solid var(--w12)", cursor: "pointer", fontFamily: FB, fontWeight: 600, fontSize: 14 }}>Close</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
